// Lint: imports reordered to satisfy import/order.
import React, { FC, useEffect } from 'react';

import { Box, HStack, Stack } from '@chakra-ui/react';
import { map, sum } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import { customTheme } from 'theme';

import {
  DetailsIdentifier,
  EllipsisText,
  StackedHeader,
} from 'components/DataDisplay';
import { BorderedBox } from 'components/DataDisplay/Box/BorderedBox';
import { DashboardTable } from 'components/DataDisplay/NewTable/DashboardTable';
import { IconTypes } from 'components/Icons';
import { Drawer } from 'components/Overlay';
import { VulnerabilityDetails } from 'containers/ActiveCloudResource/Components/ResourceMetadataDrawer/ResourceMetadata/Vulnerability/VulnerabilityDetails';
import { VulnerabilityResourceDetails } from 'containers/ActiveCloudResource/Components/ResourceMetadataDrawer/ResourceMetadata/Vulnerability/VulnerabilityResourceDetails';
import { selectActiveResource } from 'containers/ActiveCloudResource/selectors';
import { actions } from 'containers/ActiveCloudResource/slice';

import { MetadataBoxWithHeader } from './MetadataBoxWithHeader';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  resourceInsights: Record<string, any>;
  imageDetails: Record<string, any>;
  resourceDetails: Record<string, any>;
}

export const ImageVulnerabilitiesDrawer: FC<Props> = props => {
  const {
    isOpen,
    onClose,
    resourceInsights,
    imageDetails,
    resourceDetails,
  } = props;
  const dispatch = useDispatch();
  const { ecrImages } = useSelector(selectActiveResource);
  const { imageMetadata } = ecrImages;

  useEffect(() => {
    dispatch(
      actions.getContainerImageMetadata({
        q: {
          instanceId: imageDetails?.instance_id,
          containerId: imageDetails?.image_digest,
          resourceUuid: imageDetails?.resource_uuid,
        },
      }),
    );
  }, [dispatch, imageDetails]);

  const columns = [
    {
      header: 'Build Command',
      accessor: 'build_command',
      render: ({ value }) => (
        <EllipsisText fontFamily="consolas">{value}</EllipsisText>
      ),
      //styles: { w: '80%', td: { pl: 0 }, header: { pl: 0 } },
    },
  ];

  return (
    <Drawer
      isOpen={isOpen}
      onClose={onClose}
      header={
        <StackedHeader
          // Join only known parts so missing data doesn't show "undefined /".
          upper={
            imageMetadata?.data?.image_ref ||
            [
              resourceInsights?.name,
              imageDetails?.image_digest,
              imageDetails?.name || imageDetails?.image_tags?.join(', '),
            ]
              .filter(Boolean)
              .join(' / ') ||
            '-'
          }
          lower={'Container Image / Image'}
          type={IconTypes.Image}
          useCustomColor
          color={customTheme.colors.blue['300']}
          isLoading={false}
        />
      }
      body={
        <Stack h="full" w="full" spacing={5}>
          <VulnerabilityResourceDetails
            summary={imageDetails.vulnerabilities}
            details={{
              ...resourceDetails,
              containers_count: imageDetails?.containers_count,
              last_scanned_at: imageDetails?.last_scanned_at,
              created_at: imageDetails?.created_at,
              image_digest: imageDetails?.image_digest,
              image_tags: imageDetails?.image_tags,
              metadata: imageMetadata.data,
            }}
          />
          <HStack w="full" minW={0} align="stretch">
            <MetadataBoxWithHeader
              header="Image By Layer"
              flex="13 1 0"
              minW={0}
              h={56}
              pb={2}
              //pl={2}
              overflow="hidden"
            >
              <Box w="full" h="full" minW={0} minH={0} overflow="hidden">
                <DashboardTable
                  columns={columns}
                  data={map(imageMetadata?.data?.dockerfile_hint, o => ({
                    build_command: o,
                  }))}
                  isLoading={imageMetadata.isLoading}
                />
              </Box>
            </MetadataBoxWithHeader>
            <MetadataBoxWithHeader
              header="Additional Details"
              flex="7 1 0"
              minW={0}
              h={56}
            >
              <Stack justify="space-between" h="full" pt={2}>
                <DetailsIdentifier
                  label="OS"
                  value={imageMetadata?.data?.image_os}
                />
                <DetailsIdentifier
                  label="Architecture"
                  value={imageMetadata?.data?.image_architecture}
                />
                <DetailsIdentifier
                  label="Ports Open"
                  value={imageMetadata?.data?.image_exposed_ports?.join(', ')}
                />
                <DetailsIdentifier
                  label="Entry Point"
                  value={imageMetadata?.data?.image_entrypoint?.join(' ')}
                />
              </Stack>
            </MetadataBoxWithHeader>
          </HStack>
          <BorderedBox p={4} h="full">
            <Box flex={1} h="full">
              <VulnerabilityDetails
                vulnerabilityType={'library'}
                total={sum(Object.values(imageDetails?.vulnerabilities ?? {}))}
                instanceId={imageDetails?.scan_subject_id}
              />
            </Box>
          </BorderedBox>
        </Stack>
      }
      styles={{ content: { maxW: '1300px' } }}
    />
  );
};
